import {
  BarChart2,
  Users,
  ChevronRight,
  LogOut,
  Activity,
  PieChart,
  TrendingUp,
  FileText,
  Network,
  SearchCheck,
  Layers
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import logo from "../../assets/images/logo.png";
import icon from "../../assets/images/dcode-icon.png";
import { useState, useEffect, useMemo } from 'react';
import { Link } from "react-router-dom";

const Sidebar = ({ sidebarOpen, toggleSidebar, activeItem = 0, onItemClick, onCollapseChange, initialCollapsed = false }) => {
  const [hoveredItem, setHoveredItem] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(initialCollapsed);
  const [searchQuery, setSearchQuery] = useState('');
  const [projectsOpen, setProjectsOpen] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState(
    typeof window !== 'undefined' ? localStorage.getItem('selectedProjectId') : null
  );
  const navigate = useNavigate();
  const location = useLocation();
  const activePath = location.pathname;

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'selectedProjectId') {
        setSelectedProjectId(e.newValue);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Page detection helpers
  const onProjectsPage = activePath === '/project/view';

  const onProjectSettings =
    /^\/project\/settings(\/|$)/.test(activePath) ||
    /^\/project\/[^/]+\/settings(\/|$)/.test(activePath) ||
    (activePath.includes('/project') && activePath.includes('settings')) || activePath === '/project/create' || activePath === '/adhoc-code-quality-report';

  // If any child route is active, keep the Projects section open
  useEffect(() => {
    if (activePath !== '/project/view') {
      setProjectsOpen(true);
    }
  }, [activePath]);

  const onProjectsRoot = activePath.startsWith('/project');

  const hasSelectedProject = Boolean(selectedProjectId);
  const showProjectChildren = hasSelectedProject && !onProjectsPage && !onProjectSettings;

  const projectChildren = useMemo(
    () =>
      showProjectChildren
        ? [
          {
            icon: TrendingUp,
            label: 'Code quality trends',
            description: 'Code quality insights & trends',
            color: '#3B82F6',
            link: '/quality-trends',
          },
          {
            icon: Activity,
            label: 'Repository insights',
            description: 'Repository insights & trends',
            color: '#3B82F6',
            link: '/repo-insights',
          },
          {
            icon: PieChart,
            label: 'Code quality report',
            description: 'Visualize commit-specific code quality report',
            color: '#3B82F6',
            link: '/visualize/all',
          },
          {
            icon: Network,
            label: 'Analyze dependencies',
            description: 'Analyze dependency graph',
            color: '#3B82F6',
            link: '/project/analyze-dependency',
          },
          {
            icon: Layers,
            label: 'Architecture compliance',
            description: 'View architecture compliance graph',
            color: '#3B82F6',
            link: '/project/architecture-compliance',
          },
          {
            icon: SearchCheck,
            label: 'Quality delta explorer',
            description: 'Analyze difference between commit reports',
            color: '#3B82F6',
            link: '/project/quality-delta-explorer',
          },
        ]
        : [],
    [showProjectChildren]
  );

  const sidebarItems = [
    {
      icon: Users,
      label: 'Projects',
      description: 'Manage your projects',
      color: '#10B981',
      link: '/project/view',
      children: projectChildren,
    },
    {
      icon: BarChart2,
      label: 'Code quality report (adhoc)',
      description: 'Visualize adhoc code quality report',
      color: '#3B82F6',
      link: '/adhoc-code-quality-report',
    },
    {
      icon: FileText,
      label: 'Documentation',
      description: 'Explore setup guides, API references, and usage docs',
      color: '#f88381',
      link: '/docs/index.html',
    },
  ];

  const bottomItems = [
    { icon: LogOut, label: 'Sign Out', color: '#EF4444' }
  ];

  const filteredItems = sidebarItems.filter(item =>
    item.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const indexInOriginal = (filteredIndex) => {
    const filteredLabel = filteredItems[filteredIndex]?.label;
    return sidebarItems.findIndex(i => i.label === filteredLabel);
  };

  const handleItemClick = (index, e) => {
    // e.preventDefault();
    // const originalIndex = indexInOriginal(index);
    // const item = filteredItems[index];

    // if (onItemClick) onItemClick(originalIndex);
    // if (item?.link) navigate(item.link);

    const originalIndex = indexInOriginal(index);
    const item = filteredItems[index];

    if (onItemClick) onItemClick(originalIndex);

    if (item?.link) {
      // If the link points to the static docs folder, do a full page navigation
      if (item.link.startsWith('/docs')) {
        // allow browser to load the static file from /docs (no SPA routing)
        // const a = document.createElement('a');
        // a.href = item.link;
        // a.target = '_blank';
        // a.rel = 'noopener noreferrer';
        // // for compatibility trigger a click
        // a.click();
        return;
      }
      e.preventDefault();
      const navState = selectedProjectId ? { projectId: selectedProjectId } : undefined;
      navigate(item.link, { state: navState });
    } else {
      e.preventDefault();
    }
  };

  const toggleCollapse = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    if (onCollapseChange) onCollapseChange(newCollapsedState);
  };

  useEffect(() => {
    if (onCollapseChange) onCollapseChange(isCollapsed);
  }, [isCollapsed, onCollapseChange]);

  const sidebarWidth = isCollapsed ? '80px' : '280px';

  const theme = {
    bg: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
    cardBg: 'rgba(255, 255, 255, 0.9)',
    text: '#1e293b',
    textSecondary: '#64748b',
    border: 'rgba(203, 213, 225, 0.3)',
    activeBg: 'linear-gradient(135deg, #75C4C0 0%, #75C4C0 100%)',
    hoverBg: 'rgba(148, 163, 184, 0.1)',
    glassBg: 'rgba(255, 255, 255, 0.7)'
  };

  return (
    <div
      className="custom-sidebar position-fixed top-0 start-0 h-100 shadow-lg"
      style={{
        width: sidebarWidth,
        background: theme.bg,
        transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        zIndex: 1050,
        backdropFilter: 'blur(20px)',
        borderRight: `1px solid ${theme.border}`
      }}
    >
      <div className="d-flex flex-column h-100 position-relative">
        <div className="floating-circle circle-1" />
        <div className="floating-circle circle-2" />

        {/* Header */}
        <div className="d-flex justify-content-between align-items-center px-3 py-3" style={{ minHeight: '70px' }}>
          {isCollapsed ? (
            <div className="d-flex flex-column align-items-center w-100 gap-2">
              <div className="collapsed-logo">
                <img src={icon} className='sidebar-icon' alt="dcode-icon" />
                <div className="status-indicator" />
              </div>
              <button className="collapse-btn" onClick={toggleCollapse}>
                <ChevronRight size={14} />
              </button>
            </div>
          ) : (
            <>
              <div className="d-flex align-items-center">
                <Link to="/">  <img src={logo} alt="Logo" className="sidebar-logo me-2" /> </Link>
              </div>
              <button className="collapse-btn" onClick={toggleCollapse}>
                <ChevronRight size={16} style={{ transform: 'rotate(180deg)' }} />
              </button>
            </>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-grow-1 px-3 pb-3">
          {filteredItems.map((item, index) => {
            const Icon = item.icon;

            const isHovered = hoveredItem === index;

            const hasChildren = Array.isArray(item.children) && item.children.length > 0;
            const isAnyChildActive = hasChildren && item.children.some(c => activePath.startsWith(c.link));

            // Mark Projects tab active for ANY /project/* route (including settings and list)
            const isProjectsItem = item.label === 'Projects';
            const isActive =
              isProjectsItem
                ? onProjectsRoot // any project route keeps parent highlighted
                : (activePath === item.link || isAnyChildActive);

            return (
              <div key={index} className="position-relative mb-2">
                <a
                  href={item.link}
                  target={item.link && item.link.startsWith('/docs') ? '_blank' : undefined}
                  rel={item.link && item.link.startsWith('/docs') ? 'noopener noreferrer' : undefined}
                  className={`sidebar-link d-flex align-items-center text-decoration-none rounded-3 position-relative overflow-hidden mb-1 ${isActive ? 'active' : ''}`}
                  style={{
                    background: isActive ? theme.activeBg : isHovered ? theme.hoverBg : 'transparent',
                    color: isActive ? 'white' : theme.text,
                    padding: isCollapsed ? '12px' : '12px 16px',
                    justifyContent: isCollapsed ? 'center' : 'flex-start'
                  }}
                  onMouseEnter={() => setHoveredItem(index)}
                  onMouseLeave={() => setHoveredItem(null)}
                  onClick={(e) => handleItemClick(index, e)}
                >
                  {/* Hover shimmer */}
                  {isHovered && !isActive && (
                    <div className="hover-shimmer" style={{ background: `linear-gradient(90deg, transparent, ${item.color}15, transparent)` }} />
                  )}

                  {/* Icon */}
                  <div
                    className="icon-wrapper"
                    style={{
                      background: isActive ? 'rgba(255,255,255,0.2)' : `${item.color}20`,
                      color: isActive ? 'white' : item.color,
                      marginRight: isCollapsed ? '0' : '12px'
                    }}
                  >
                    <Icon size={16} />
                  </div>

                  {/* Label + desc */}
                  {!isCollapsed && (
                    <>
                      <div className="flex-grow-1">
                        <div className="fw-medium" style={{ fontSize: '14px' }}>
                          {item.label}
                        </div>
                        <div className="small" style={{ color: isActive ? 'rgba(255,255,255,0.8)' : theme.textSecondary, fontSize: '11px' }}>
                          {item.description}
                        </div>
                      </div>

                      {/* Chevron to toggle submenu (if any).
                          Hide chevron entirely on settings page so user can’t expand. */}
                      {hasChildren && !onProjectSettings && (
                        <button
                          className="btn btn-sm border-0"
                          style={{ color: isActive ? 'white' : theme.textSecondary }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setProjectsOpen((v) => !v);
                          }}
                          aria-label={projectsOpen ? 'Collapse Projects' : 'Expand Projects'}
                          title={projectsOpen ? 'Collapse' : 'Expand'}
                        >
                          <ChevronRight
                            size={14}
                            style={{
                              transform: projectsOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s ease'
                            }}
                          />
                        </button>
                      )}
                    </>
                  )}
                </a>

                {/* Submenu — only render when allowed */}
                {item.label === 'Projects' && showProjectChildren && projectsOpen && !isCollapsed && (
                  <div className="ms-4 mt-1">
                    {item.children.map((child, cIdx) => {
                      const ChildIcon = child.icon;
                      const childKey = `child-${index}-${cIdx}`;
                      const childHovered = hoveredItem === childKey;
                      const childActive = activePath.startsWith(child.link);

                      return (
                        <a
                          key={cIdx}
                          href={child.link}
                          className="d-flex align-items-center rounded-3 text-decoration-none mb-1"
                          style={{
                            padding: '8px 12px',
                            background: childActive ? theme.activeBg : (childHovered ? theme.hoverBg : 'transparent'),
                            color: childActive ? 'white' : theme.text,
                            fontSize: '13px',
                            fontWeight: childActive ? 600 : 400
                          }}
                          onMouseEnter={() => setHoveredItem(childKey)}
                          onMouseLeave={() => setHoveredItem(null)}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(child.link);
                          }}
                        >
                          <span
                            className="me-2 d-inline-flex align-items-center justify-content-center"
                            style={{
                              width: 26,
                              height: 26,
                              borderRadius: 8,
                              background: childActive ? 'rgba(255,255,255,0.2)' : `${child.color}20`,
                              color: childActive ? 'white' : child.color
                            }}
                          >
                            <ChildIcon size={14} />
                          </span>
                          <div className="flex-grow-1">
                            <div className="fw-medium">{child.label}</div>
                            {child.description && (
                              <div className="small" style={{ color: childActive ? 'rgba(255,255,255,0.8)' : theme.textSecondary, fontSize: 11 }}>
                                {child.description}
                              </div>
                            )}
                          </div>
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Bottom */}
        <div className="px-3 py-3" style={{ borderTop: `1px solid ${theme.border}` }}>
          {bottomItems.map((item, index) => {
            const Icon = item.icon;
            const bottomIndex = sidebarItems.length + index;
            const isHovered = hoveredItem === bottomIndex;

            return (
              <a
                key={bottomIndex}
                href={item.link || '#'}
                className="d-flex align-items-center text-decoration-none rounded-3 mb-1"
                style={{
                  background: isHovered ? theme.hoverBg : 'transparent',
                  color: theme.text,
                  fontSize: '14px',
                  padding: isCollapsed ? '8px' : '8px 12px',
                  justifyContent: isCollapsed ? 'center' : 'flex-start'
                }}
                onMouseEnter={() => setHoveredItem(bottomIndex)}
                onMouseLeave={() => setHoveredItem(null)}
                onClick={(e) => {
                  e.preventDefault();
                  if (item.label === 'Sign Out') {
                    localStorage.removeItem("Token");
                    localStorage.removeItem("Email");
                    navigate("/login");
                  } else if (item.link) {
                    navigate(item.link);
                  }
                }}
              >
                <Icon size={16} style={{ color: item.color, marginRight: isCollapsed ? '0' : '12px' }} />
                {!isCollapsed && <span>{item.label}</span>}
              </a>
            );
          })}

          {!isCollapsed && (
            <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${theme.border}` }}>
              <div className="small text-center" style={{ color: theme.textSecondary }}>
                © 2025 DCode
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
